// Shared, mutable view state. A single object so cross-module writes (selectedFarm,
// currentUserRole, …) are visible everywhere without re-export gymnastics — ES module
// bindings can't be reassigned across modules, but object properties can.
export const appState = {
    allDevices: new Map(),
    selectedFarm: null,
    selectedFarmId: null,
    currentUserRole: 'operator',
    currentUserLogin: '',
    // The current user's own id (from /api/auth/me). Used to lock self-edits (e.g.
    // a user may not change their own role). null until /me resolves.
    currentUserId: null,
    // The current user's single group (from /api/auth/me). null when ungrouped.
    currentUserTeamId: null,
    currentUserTeamName: null,
    // True once the first device-list snapshot has arrived over the WS. Used to
    // tell "loading" apart from "genuinely empty" right after a page refresh.
    devicesLoaded: false,
};
// Set the selected farm by name, clearing the cached farm id whenever the name
// actually changes so device→farm matching never uses an id left over from a
// previously-viewed farm. The id is re-resolved (from the farm record) when the
// grid renders; a rename navigation can set it explicitly right after this. Pass
// the id when it's known at the same moment (e.g. a farm-row click).
export function setSelectedFarm(name, id = null) {
    if (appState.selectedFarm !== name)
        appState.selectedFarmId = null;
    appState.selectedFarm = name;
    if (id != null)
        appState.selectedFarmId = id;
}
// Per-device custom display names (aliases), stored server-side (DB) and shared
// across all users. Loaded once into this in-memory map at startup so device
// cards can render the alias synchronously (mirrors `commentCache`).
export const deviceAliases = new Map();
export async function loadDeviceNames() {
    try {
        const res = await fetch('/api/device-names');
        if (!res.ok)
            return;
        const data = await res.json();
        deviceAliases.clear();
        for (const [udid, name] of Object.entries(data.names ?? {}))
            deviceAliases.set(udid, name);
    }
    catch { /* ignore — cards fall back to the model name */ }
}
// Per-device comment cache, shared by the card hover-tooltip and the comment modals.
export const commentCache = new Map();
// Per-farm "turn the screen off when a stream connects" preference, keyed by farm
// name (devices reference their farm by name). Loaded from /api/farms at startup
// and refreshed whenever the farm list is fetched; the inline-stream connect path
// reads it synchronously to decide whether to auto-blank the device on connect.
export const farmScreenOff = new Map();
export async function loadFarmScreenOff() {
    try {
        const res = await fetch('/api/farms');
        if (!res.ok)
            return;
        const list = await res.json();
        farmScreenOff.clear();
        for (const f of list)
            farmScreenOff.set(f.name, !!f.screen_off_on_connect);
    }
    catch { /* ignore — auto screen-off simply won't fire until next load */ }
}
